<template>
  <div class="tw-relative">
    <StudioLabel v-if="label || lengthCounter" :label="label" :lengthCounter="lengthCounter" :htmlFor="id" />
    <div class="tw-flex tw-items-center">
      <div
        :class="{
          'tw-relative tw-flex tw-flex-grow tw-rounded-md dark:tw-bg-black': true,
          'tw-cursor-not-allowed': disabled,
          'hover:tw-outline-deep-orange-500': !disabled,
          'focus-within:tw-outline-2 focus-within:tw-outline-deep-orange-500':
            !disabled && !inlineError && !inlineSuccess,
          '!tw-border-red-600 focus:tw-border-red-400 focus:tw-ring-red-400 dark:!tw-border-red-400': inlineError,
          '!tw-border-green-600 focus:tw-border-green-400 focus:tw-ring-green-400 dark:!tw-border-green-400':
            inlineSuccess,
          [extraInputClasses]: extraInputClasses
        }"
      >
        <input
          :id="id"
          ref="input"
          :class="{
            'tw-block tw-w-full tw-appearance-none tw-rounded-md tw-border tw-border-solid tw-bg-white tw-px-3 tw-py-2 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 dark:tw-bg-black dark:tw-text-white sm:tw-text-sm': true,
            'tw-cursor-not-allowed': disabled,
            'hover:tw-border-deep-orange-500 focus:tw-border-deep-orange-600 focus:tw-ring-deep-orange-500':
              !disabled && !inlineError && !inlineSuccess,
            '!tw-border-red-600 focus:tw-border-red-400 focus:tw-ring-red-400 dark:!tw-border-red-400': inlineError,
            '!tw-border-green-600 focus:tw-border-green-400 focus:tw-ring-green-400 dark:!tw-border-green-400':
              inlineSuccess
          }"
          :type="type"
          :data-testid="id"
          :placeholder="placeholder"
          :disabled="disabled"
          :title="disabledReason"
          :required="required"
          :value="modelValue"
          :minlength="minlength"
          :maxlength="maxlength"
          :autocomplete="autocomplete"
          @input="emitModelValueUpdate"
          @change="emitModelValueUpdate"
          @focus="$emit('focus', $event)"
          @blur="$emit('blur', $event)"
        />
        <button
          v-if="showCopyToClipboard"
          type="button"
          class="tw-absolute tw-right-1 tw-top-1.5 tw-bg-white tw-px-2 tw-py-1 tw-text-slate-400"
          :disabled="copiedStatus !== 'not-copied'"
          @click="handleCopyToClipboard"
        >
          <DocumentDuplicate v-if="copiedStatus === 'not-copied'" class="tw-h-4 tw-w-4" />
          <p v-else-if="copiedStatus === 'copied'" class="tw-text-xs">Copied!</p>
          <p v-else-if="copiedStatus === 'failed'" class="tw-text-xs">Failed</p>
        </button>
      </div>
      <slot name="action" />
    </div>
    <div v-if="inlineError" class="tw-mt-1 tw-text-xs tw-text-red-600 dark:tw-text-red-400">
      {{ inlineError }}
    </div>
    <div v-else-if="inlineSuccess" class="tw-mt-1 tw-text-xs tw-text-green-600 dark:tw-text-green-400">
      {{ inlineSuccess }}
    </div>
  </div>
</template>

<script>
import logger from '@goldcast/logger';
import { sanitizeString } from '@goldcast/utils';
import { defineComponent } from 'vue';

import { DocumentDuplicate } from './Icons';
import StudioLabel from './StudioLabel.vue';

const CHAR_THRESHOLD = 10;

export default defineComponent({
  name: 'StudioInput',
  components: { DocumentDuplicate, StudioLabel },
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    type: {
      type: String,
      default: 'text',
      validator: prop => ['text', 'email', 'password', 'number'].includes(prop)
    },
    placeholder: { type: String, default: '' },
    label: { type: String, default: '' },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    disabledReason: { type: String, default: '' },
    id: { type: String, required: true },
    autocomplete: {
      type: String,
      default: 'off',
      validator: prop => ['off', 'on', 'email', 'current-password'].includes(prop)
    },
    minlength: { type: Number, default: Infinity },
    maxlength: { type: Number, default: Infinity },
    inlineError: { type: String, default: '' },
    inlineSuccess: { type: String, default: '' },
    showCopyToClipboard: { type: Boolean, default: false },
    extraInputClasses: { type: String, default: '' },
    autofocus: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'focus', 'blur'],
  data() {
    return {
      copiedStatus: 'not-copied' // 'copied' | 'failed' | 'not-copied'
    };
  },
  mounted() {
    if (this.autofocus) {
      this.focus();
    }
  },
  computed: {
    lengthCounter() {
      if (this.maxlength < Infinity && this.maxlength - this.modelValue?.length <= CHAR_THRESHOLD) {
        return `(${this.modelValue?.length || 0}/${this.maxlength})`;
      }
      return '';
    }
  },
  methods: {
    /** @public */
    checkValidity() {
      return this.$refs.input.checkValidity();
    },
    /** @public */
    setCustomValidity(msg) {
      const input = this.$refs.input;
      input.setCustomValidity(msg);
      input.reportValidity();
    },
    /** @public */
    focus() {
      this.$refs.input.focus();
    },
    handleCopyToClipboard(e) {
      e.stopPropagation();
      navigator.clipboard
        .writeText(this.modelValue)
        .then(() => (this.copiedStatus = 'copied'))
        .catch(() => (this.copiedStatus = 'failed'))
        .finally(() =>
          setTimeout(() => {
            this.copiedStatus = 'not-copied';
          }, 2000)
        );
    },
    emitModelValueUpdate($event) {
      const value = $event.target.value;
      this.$emit('update:modelValue', sanitizeString(value, logger));
    }
  }
});
</script>
